import { fmtDistanceToNow } from '@/lib/dates'
import { Play, Pause, ArrowRight, Users, UserPlus, Send, MessageSquare, Calendar, Bot } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import type { Campaign } from './types'
import { useActivateCampaign, usePauseCampaign } from './use-campaigns'
import { STATUS_BADGE, CAMPAIGN_STATUS_VARIANT } from '@/lib/status-styles'
import { readMessageWriter } from '@/features/settings/mcp-helpers'

/** Shown on campaigns whose messages are written by the user's Claude (settings.message_writer 'mcp'). */
export function ClaudeWritesBadge({ campaign }: { readonly campaign: Pick<Campaign, 'settings'> }) {
  if (readMessageWriter(campaign) !== 'mcp') return null
  return (
    <Badge variant="outline" className="gap-1 text-[10px]" title="Your Claude writes this campaign's messages">
      <Bot className="h-3 w-3" />
      Claude writes
    </Badge>
  )
}

type CampaignStatusBadgeProps = {
  readonly status: Campaign['status']
}

const STATUS_LABELS: Record<Campaign['status'], string> = {
  active: 'Active',
  paused: 'Paused',
  draft: 'Draft',
  completed: 'Completed',
  deleted: 'Deleted',
}

function CampaignStatusBadge({ status }: CampaignStatusBadgeProps) {
  const variant = CAMPAIGN_STATUS_VARIANT[status] ?? 'neutral'
  return <Badge className={STATUS_BADGE[variant]}>{STATUS_LABELS[status]}</Badge>
}

type StatItemProps = {
  readonly icon: React.ReactNode
  readonly label: string
  readonly value: number | string
}

function StatItem({ icon, label, value }: StatItemProps) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-1 text-center">
      <div className="flex items-center gap-1 text-muted-foreground">{icon}</div>
      <span className="text-base font-semibold tabular-nums">{value}</span>
      {/* Three columns on phones, five from 420px (~60px per cell), so the
          two-word labels wrap. Reserving two lines keeps the baselines aligned. */}
      <span className="min-h-[2.1em] text-[10px] leading-tight text-muted-foreground">
        {label}
      </span>
    </div>
  )
}

type CampaignCardProps = {
  readonly campaign: Campaign
  readonly onViewDetails: (id: string) => void
}

export function CampaignCard({ campaign, onViewDetails }: CampaignCardProps) {
  const activate = useActivateCampaign()
  const pause = usePauseCampaign()

  const replyRate =
    campaign.sent_count > 0
      ? ((campaign.replied_count / campaign.sent_count) * 100).toFixed(1)
      : '0.0'

  const meetingRate =
    campaign.replied_count > 0
      ? ((campaign.meeting_count / campaign.replied_count) * 100).toFixed(1)
      : '0.0'

  const lastActivity = fmtDistanceToNow(campaign.updated_at, { addSuffix: true })

  const isActivating = activate.isPending
  const isPausing = pause.isPending

  function handleActivate() {
    activate.mutate(campaign.id)
  }

  function handlePause() {
    pause.mutate(campaign.id)
  }

  return (
    <Card className="flex flex-col gap-0">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{campaign.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {campaign.sequence_config.steps.length}-step sequence
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <CampaignStatusBadge status={campaign.status} />
            <ClaudeWritesBadge campaign={campaign} />
            {campaign.brief_required && !campaign.brief_confirmed_at && (
              <Badge variant="outline" className="border-amber-500/50 text-[10px] text-amber-600 dark:text-amber-400">
                Needs messaging brief
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {/* Stats row */}
        {/* Stat row mirrors the funnel order. `sent_count` counts leads at
            `connection_sent` or beyond (connection requests), `contacted_count`
            counts leads that got their first DM or beyond (messages) — the old
            single "Sent" tile showed the former under a label that read as the
            latter. See computeStats() in packages/db/src/queries/campaigns.ts. */}
        <div className="grid grid-cols-3 gap-y-3 rounded-lg border bg-muted/30 py-3 min-[420px]:grid-cols-5 min-[420px]:gap-y-0 min-[420px]:divide-x">
          <StatItem
            icon={<Users className="h-3.5 w-3.5" />}
            label="Leads"
            value={campaign.leads_count}
          />
          <StatItem
            icon={<UserPlus className="h-3.5 w-3.5" />}
            label="Connections Sent"
            value={campaign.sent_count}
          />
          <StatItem
            icon={<Send className="h-3.5 w-3.5" />}
            label="Leads Messaged"
            value={campaign.contacted_count}
          />
          <StatItem
            icon={<MessageSquare className="h-3.5 w-3.5" />}
            label="Replies"
            value={campaign.replied_count}
          />
          <StatItem
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="Meetings"
            value={campaign.meeting_count}
          />
        </div>

        {/* Rate badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Reply rate:</span>
          <Badge variant="outline" className="text-xs">
            {replyRate}%
          </Badge>
          <span className="text-xs text-muted-foreground ml-2">Meeting rate:</span>
          <Badge variant="outline" className="text-xs">
            {meetingRate}%
          </Badge>
        </div>

        {activate.isError && (
          <p className="text-xs text-destructive">
            {activate.error instanceof Error ? activate.error.message : 'Could not activate this campaign.'}
          </p>
        )}

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t">
          <span className="text-xs text-muted-foreground">Updated {lastActivity}</span>

          <div className="ml-auto flex items-center gap-2">
            {campaign.status === 'active' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePause}
                disabled={isPausing}
              >
                <Pause className="h-3.5 w-3.5 mr-1" />
                {isPausing ? 'Pausing...' : 'Pause'}
              </Button>
            ) : campaign.status !== 'completed' && campaign.status !== 'deleted' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleActivate}
                disabled={isActivating}
              >
                <Play className="h-3.5 w-3.5 mr-1" />
                {isActivating ? 'Activating...' : 'Activate'}
              </Button>
            ) : null}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(campaign.id)}
            >
              View
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
